import { useState, useEffect } from "react";
import { Check, AlertCircle, Loader2 } from "lucide-react";
import { supabase } from "../../lib/supabase";

const SLUG_RE = /^[a-z0-9][a-z0-9-]{2,39}$/;
const RESERVED = new Set([
  "admin", "api", "auth", "login", "logout", "l", "public",
  "perfil", "herramientas", "documentos", "pagos", "sesiones",
  "actividades", "dashboard", "privacy", "privacidad", "gracias",
  "static", "assets",
]);

function validateLocally(slug) {
  if (!slug) return "Indica un nombre para tu enlace";
  if (!SLUG_RE.test(slug)) {
    return "Solo minúsculas, números y guiones (3-40 caracteres). Empieza por letra o número.";
  }
  if (RESERVED.has(slug)) return "Ese nombre está reservado, elige otro.";
  return null;
}

export default function LeadCaptureSlugField({
  value,
  onChange,
  currentSlug,
  disabled,
}) {
  const [checking, setChecking] = useState(false);
  const [available, setAvailable] = useState(null);
  const [localError, setLocalError] = useState(null);

  useEffect(() => {
    setLocalError(validateLocally(value));
    setAvailable(null);

    if (!value || validateLocally(value)) return;
    if (value === currentSlug) {
      setAvailable(true);
      return;
    }

    let alive = true;
    setChecking(true);
    const t = setTimeout(async () => {
      const { data, error } = await supabase.rpc(
        "is_lead_capture_slug_taken",
        { slug_to_check: value },
      );
      if (alive) {
        if (error) setAvailable(null);
        else setAvailable(data === false);
        setChecking(false);
      }
    }, 400);

    return () => {
      alive = false;
      clearTimeout(t);
      setChecking(false);
    };
  }, [value, currentSlug]);

  const showAvailable = !localError && available === true && !checking;
  const showTaken = !localError && available === false && !checking;

  return (
    <div>
      <label className="block text-sm font-medium text-gray-700 mb-1.5">
        Tu enlace
      </label>
      <div className="flex items-stretch gap-0">
        <span className="inline-flex items-center px-3 bg-gray-50 border border-r-0 border-gray-300 rounded-l-lg text-sm text-gray-500 truncate">
          {window.location.origin}/l/
        </span>
        <input
          type="text"
          value={value}
          onChange={(e) =>
            onChange(e.target.value.toLowerCase().slice(0, 40))
          }
          disabled={disabled}
          maxLength={40}
          placeholder="mi-centro"
          className="flex-1 min-w-0 px-3 py-2 border border-gray-300 rounded-r-lg text-sm focus:outline-none focus:ring-2 focus:ring-brand/30 focus:border-brand disabled:bg-gray-50"
        />
      </div>

      <div className="min-h-[1.25rem] mt-1 text-xs flex items-center gap-1.5">
        {checking && (
          <span className="text-gray-500 flex items-center gap-1">
            <Loader2 size={12} className="animate-spin" /> Comprobando…
          </span>
        )}
        {localError && (
          <span className="text-red-600 flex items-center gap-1">
            <AlertCircle size={12} /> {localError}
          </span>
        )}
        {showAvailable && (
          <span className="text-emerald-600 flex items-center gap-1">
            <Check size={12} /> Disponible
          </span>
        )}
        {showTaken && (
          <span className="text-red-600 flex items-center gap-1">
            <AlertCircle size={12} /> Ya está cogido
          </span>
        )}
      </div>
    </div>
  );
}

export function isSlugValid(slug, isAvailable) {
  return !validateLocally(slug) && isAvailable !== false;
}
